import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  updateNotes as updateNotesApi,
  updateTimelineMarkers as updateMarkersApi,
  updateRecordingMetadata as updateMetadataApi,
} from '../../../services/workspaceService';

/** Notes are typed, so they debounce; markers and metadata are single gestures. */
const NOTES_DEBOUNCE_MS = 800;

/**
 * The workspace-level annotations: notes, timeline markers, and the per-recording
 * colour/label map. Each is stored on the workspace rather than in layoutConfig,
 * so they have their own endpoints and their own save behaviour.
 */
export default function useWorkspaceAnnotations(workspaceId, trackSave) {
  const [globalNotes, setGlobalNotes] = useState('');
  const [globalMarkers, setGlobalMarkers] = useState([]);
  const [recordingMetadata, setRecordingMetadata] = useState({});
  const saveNotesTimer = useRef(null);
  const pendingNotes = useRef(null);

  // The metadata map as it stands right now, for callbacks that outlive the
  // render they were created in — the undo of a recording removal is one, and
  // merging against its own render's map would resurrect the entry the removal
  // had just deleted.
  const metadataRef = useRef(recordingMetadata);
  metadataRef.current = recordingMetadata;

  /** Write the notes that are waiting, now. */
  const flushNotes = useCallback(() => {
    if (saveNotesTimer.current) {
      clearTimeout(saveNotesTimer.current);
      saveNotesTimer.current = null;
    }
    const notes = pendingNotes.current;
    if (notes === null) return;
    pendingNotes.current = null;
    trackSave(updateNotesApi(workspaceId, notes)).catch(() => toast.error('Failed to save notes'));
  }, [workspaceId, trackSave]);

  const updateGlobalNotes = useCallback((notes) => {
    setGlobalNotes(notes);
    pendingNotes.current = notes;
    if (saveNotesTimer.current) clearTimeout(saveNotesTimer.current);
    saveNotesTimer.current = setTimeout(flushNotes, NOTES_DEBOUNCE_MS);
  }, [flushNotes]);

  // Leaving the workspace mid-sentence used to drop what was typed.
  useEffect(() => flushNotes, [flushNotes]);

  const updateGlobalMarkers = useCallback((markers) => {
    setGlobalMarkers(markers);
    trackSave(updateMarkersApi(workspaceId, markers)).catch(() => toast.error('Failed to save markers'));
  }, [workspaceId, trackSave]);

  const updateRecordingMetadata = useCallback((metadata) => {
    metadataRef.current = metadata;
    setRecordingMetadata(metadata);
    trackSave(updateMetadataApi(workspaceId, metadata))
      .catch(() => toast.error('Failed to save recording metadata'));
  }, [workspaceId, trackSave]);

  /** Merge one recording's entry and persist the whole map. */
  const patchRecordingMetadata = useCallback((recordingId, patch) => {
    updateRecordingMetadata({
      ...metadataRef.current,
      [recordingId]: { ...metadataRef.current[recordingId], ...patch },
    });
  }, [updateRecordingMetadata]);

  return {
    globalNotes,
    globalMarkers,
    recordingMetadata,
    setGlobalNotes,
    setGlobalMarkers,
    setRecordingMetadata,
    updateGlobalNotes,
    updateGlobalMarkers,
    updateRecordingMetadata,
    patchRecordingMetadata,
  };
}
